import callAPI from '../../API/callSecureAPI'
import endpoints from '../../API/endpoints'
import { useAuth0 } from '../../contexts/auth0-context'
import { isIntegrationEnabled } from '../constants/integrations'
import { useIsOtsukaOrganization } from '../hooks/useIsOtsukaOrganization'
import { useStrategy } from '../hooks/useStrategy'
import { useGetOrganizationQuery } from './useGetOrganizationQuery'
import { ExternalActivitySearchResponse } from './useSearchExternalActivitiesQuery'
import {
    ExternalActivitySource,
    useLinkActivityToExternalSourceMutation,
} from './useLinkActivityToExternalSourceMutation'

type AutoLinkParams = {
    activityId: string
    roadmapId: string
    studyNumber?: string
    // The activity's current source; activities that already have one are skipped.
    source?: string | null
}

/**
 * Silent activity-to-external-source linking, shared by the activity create and
 * edit forms. After a save, looks the study number up in GSL/Planisware and, on
 * an exact match, links the activity to that source — no user interaction. A
 * study found in Planisware wins over GSL. Failures are non-fatal: the save has
 * already succeeded, so errors are only logged. Never runs on a sandbox roadmap,
 * whose activities must stay out of GSL/Planisware.
 */
export const useAutoLinkActivityToExternalSource = () => {
    const { getTokenSilently } = useAuth0()
    const { data: organization } = useGetOrganizationQuery()
    const { mutateAsync: linkActivityToExternalSource } = useLinkActivityToExternalSourceMutation()
    const isOtsukaOrganization = useIsOtsukaOrganization()
    const strategy = useStrategy()

    // Auto-linking is rolled out to Otsuka only for now, and requires at least
    // one external integration to be enabled for the org.
    const canLinkExternalSource =
        isOtsukaOrganization &&
        !strategy?.isSandbox &&
        (isIntegrationEnabled(organization?.integrations, 'gsl') ||
            isIntegrationEnabled(organization?.integrations, 'planisware'))

    const autoLinkActivityToExternalSource = async ({ activityId, roadmapId, studyNumber, source }: AutoLinkParams) => {
        if (source || !canLinkExternalSource || !studyNumber?.trim()) return
        try {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            const searchResponse = (await callAPI(
                endpoints.searchExternalActivities.url,
                endpoints.searchExternalActivities.method,
                token,
                { studyId: studyNumber, limit: 10 }
            )) as ExternalActivitySearchResponse

            const target = studyNumber.trim().toLowerCase()
            const exactMatches = (searchResponse?.results ?? []).filter(
                (r) => (r.studyNumber ?? '').trim().toLowerCase() === target
            )
            const match =
                exactMatches.find((r) => r.source === 'Planisware') ?? exactMatches.find((r) => r.source === 'GSL')
            if (!match) return

            await linkActivityToExternalSource({
                activityId,
                roadmapId,
                studyNumber: match.studyNumber as string,
                source: match.source as ExternalActivitySource,
            })
        } catch (err) {
            console.warn('Failed to link activity to external source', err)
        }
    }

    return { autoLinkActivityToExternalSource }
}
